/**
 * Buscador por disfraz (Optioutlet).
 *
 * El buscador de Shopify compara palabras contra títulos y etiquetas. Sirve
 * cuando alguien ya sabe qué quiere ("rojo vampiro") y no sirve para nada
 * cuando sólo sabe de qué se va a disfrazar, que es el caso normal en
 * octubre. Esta pestaña recibe la idea en español corriente — "voy de bruja",
 * "algo que dé miedo pero sin sangre" — y devuelve tres modelos con el
 * porqué de cada uno.
 *
 * Quién decide: un Worker nuestro (carpeta `buscador-ia/`) que le pregunta a
 * Claude con el catálogo completo adentro. El tema no sabe nada del modelo ni
 * de la llave; sólo manda texto y recibe handles.
 *
 * Los datos de cada tarjeta salen de `/products/{handle}.js`, que es público y
 * lo sirve el CDN de Shopify. Así el precio y la foto siempre son los de hoy,
 * aunque el catálogo del Worker se quede un poco atrás.
 */
// Con Haiku una consulta tardaba ~3.4s y 12s sobraban. Opus piensa antes de
// elegir y se va a 4–8s, así que el reloj se queda corto justo cuando la
// respuesta iba a llegar. 20s deja margen sin que una caída real se eternice.
const ESPERA = 20000;

// Las rutas de los dos logos. El tema las pone en el elemento raíz con
// `asset_url`, porque el CDN de Shopify les cuelga un hash de versión que
// desde JavaScript no hay forma de adivinar.
const ICONOS = { ml: '', az: '' };

const escapar = (s) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// "Pupilentes Amarillo Gato — Combo Halloween" en una tarjeta de 64px de ancho
// se parte en tres renglones y no dice nada: ya estamos dentro de una tienda de
// pupilentes y todos son combo. Queda "Amarillo Gato".
const nombreCorto = (titulo) =>
  String(titulo).replace(/^Pupilentes\s+/i, '').replace(/\s*—\s*Combo Halloween$/i, '');

// Con espacio después del signo, que es como lo escribe el tema ("$ 399.99").
// Las dos listas viven en el mismo modal y la diferencia se nota renglón a renglón.
const dinero = (centavos) =>
  `$ ${(centavos / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Shopify devuelve las fotos sin protocolo y en tamaño original. Se pide el ancho
// que de verdad va a pintarse: 200 para la fila del modal (marco de 84px) y 600
// para la tarjeta del Oráculo, que llega a ~300px y se ve en pantalla retina.
const foto = (url, ancho = 200) =>
  url ? `${url.startsWith('//') ? 'https:' : ''}${url}`.replace(/(\.[a-z]+)(\?|$)/i, `_${ancho}x$1$2`) : '';

// En las tarjetas se pinta la foto del ojo, no la portada del combo. En un marco
// de 84px la caja con la solución y los tatuajes se vuelve una mancha; el ojo
// llena el encuadre y se distingue de un golpe. Se reconoce por el sufijo del
// archivo, que es la convención de la tienda para esa toma. Los productos que no
// la tienen --los NEON y los colores nuevos de 2026-- se caen a la portada.
const ojo = (p) => p.images?.find((u) => u.includes('-packshot')) || p.featured_image;

// Con Opus la espera se fue a 4–8 segundos. Un solo texto fijo todo ese rato
// parece que se trabó; el oráculo hablando mientras piensa hace que la espera
// se sienta parte del personaje y no una falla.
const FRASES = [
  'Estoy pensando…',
  'Veamos qué te irá mejor…',
  'Consultando la bola…',
  'Repasando los sesenta y cinco modelos…',
  'Ya casi lo tengo…',
];

/**
 * Pinta el estado de espera y va cambiando la frase. Devuelve la función que lo
 * detiene: el intervalo tiene que morir aunque la respuesta llegue antes de la
 * siguiente frase, o se queda corriendo contra un nodo que ya no está en la
 * página.
 */
function pensando(salida) {
  salida.innerHTML = `
    <p class="ol-ia__pensando">
      <span class="ol-ia__puntos"><i></i><i></i><i></i></span>
      <span class="ol-ia__frase" data-ol-frase>${FRASES[0]}</span>
    </p>`;

  const nodo = salida.querySelector('[data-ol-frase]');
  let i = 0;
  const reloj = setInterval(() => {
    if (!nodo.isConnected) return clearInterval(reloj);
    i = (i + 1) % FRASES.length;
    nodo.classList.add('ol-ia__frase--sale');
    setTimeout(() => {
      if (!nodo.isConnected) return;
      nodo.textContent = FRASES[i];
      nodo.classList.remove('ol-ia__frase--sale');
    }, 260);
  }, 1800);

  return () => clearInterval(reloj);
}

function error(salida, texto) {
  salida.innerHTML = `<p class="ol-ia__nota">${escapar(texto)}</p>`;
}

// Las dos cosas se piden al mismo tiempo. `/products/{handle}.js` trae precio,
// foto y título, pero NO los metafields, así que las ligas de marketplace hay
// que ir a buscarlas a `?view=ol-mp` (ver templates/product.ol-mp.liquid).
// Si esa segunda falla no pasa nada: la tarjeta sale sin sellos.
async function producto(handle) {
  const raiz = window.Shopify?.routes?.root ?? '/';
  try {
    const [datos, ligas] = await Promise.all([
      fetch(`${raiz}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${raiz}products/${handle}?view=ol-mp`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]);
    if (!datos) return null;
    return { ...datos, ol_ml: ligas?.ml || '', ol_az: ligas?.az || '' };
  } catch {
    return null;
  }
}

// Los sellos. Van como HERMANOS del enlace de la tarjeta, nunca dentro: la
// tarjeta entera ya es un <a> y un enlace dentro de otro enlace es HTML
// inválido --el navegador parte el marcado y el resultado es impredecible.
// El CSS los encima con position:absolute.
function sellos(p, estilo) {
  if (!p.ol_ml && !p.ol_az) return '';
  const uno = (liga, icono, tienda) =>
    liga
      ? `<a class="ol-mps__sello ol-mps__sello--${tienda === 'Amazon' ? 'amazon' : 'ml'}"
           href="${escapar(liga)}" target="_blank" rel="noopener"
           aria-label="Comprar ${escapar(nombreCorto(p.title))} en ${tienda}"
         ><img src="${escapar(icono)}" alt="" loading="lazy"></a>`
      : '';
  return `<span class="ol-mps${estilo === 'fila' ? ' ol-mps--fila' : ''}">
    ${uno(p.ol_ml, ICONOS.ml, 'Mercado Libre')}${uno(p.ol_az, ICONOS.az, 'Amazon')}
  </span>`;
}

function tarjeta(p, razon, i) {
  return `
    <li class="ol-ia__fila">
      <a class="ol-ia__tarjeta" href="${escapar(p.url)}" style="--i: ${i}">
        <span class="ol-ia__marco">
          <img class="ol-ia__foto" src="${escapar(foto(ojo(p)))}" alt="" loading="lazy">
        </span>
        <div>
          <p class="ol-ia__nombre">${escapar(nombreCorto(p.title))}</p>
          <p class="ol-ia__razon">${escapar(razon)}</p>
          <p class="ol-ia__precio">
            ${p.compare_at_price && p.compare_at_price > p.price
              ? `<s class="ol-ia__antes">${escapar(dinero(p.compare_at_price))}</s> `
              : ''}${escapar(dinero(p.price))}
          </p>
        </div>
      </a>
      ${sellos(p, 'fila')}
    </li>`;
}

/**
 * La tarjeta grande del Oráculo: foto cuadrada arriba, como las de "Los más
 * buscados". En el modal no cabría; aquí la sección es ancha y el producto se
 * puede lucir, que es justo lo que pidió Brandon.
 */
function tarjetaGrande(p, razon, i) {
  return `
    <li class="ol-oraculo__celda">
      <a class="ol-oraculo__card" href="${escapar(p.url)}" style="--i: ${i}">
        <span class="ol-oraculo__card-foto">
          <img src="${escapar(foto(ojo(p), 600))}" alt="" loading="lazy">
        </span>
        <p class="ol-oraculo__card-nombre">${escapar(nombreCorto(p.title))}</p>
        <p class="ol-oraculo__card-razon">${escapar(razon)}</p>
        <p class="ol-oraculo__card-precio">
          ${p.compare_at_price && p.compare_at_price > p.price
            ? `<s class="ol-ia__antes">${escapar(dinero(p.compare_at_price))}</s> `
            : ''}${escapar(dinero(p.price))}
        </p>
      </a>
      ${sellos(p, 'tarjeta')}
    </li>`;
}

async function pintar(raiz, salida, datos, estilo = 'fila') {
  if (!datos.recomendaciones.length) {
    salida.innerHTML = `<p class="ol-ia__mensaje">${escapar(datos.mensaje)}</p>`;
    return [];
  }

  raiz.classList.add('ol-ia--respondido');

  // Los tres se piden al mismo tiempo; esperar en fila sumaría tres viajes.
  const productos = await Promise.all(datos.recomendaciones.map((r) => producto(r.handle)));

  const dibujar = estilo === 'tarjeta' ? tarjetaGrande : tarjeta;
  const filas = datos.recomendaciones
    .map((r, i) => (productos[i] ? dibujar(productos[i], r.razon, i) : ''))
    .filter(Boolean);

  if (!filas.length) {
    error(salida, 'Encontramos modelos pero no pudimos cargarlos. Vuelve a intentar.');
    return [];
  }

  salida.innerHTML = `
    <p class="ol-ia__mensaje">${escapar(datos.mensaje)}</p>
    <ul class="${estilo === 'tarjeta' ? 'ol-oraculo__lista' : 'ol-ia__lista'}">${filas.join('')}</ul>`;

  return datos.recomendaciones.filter((r, i) => productos[i]).map((r) => r.handle);
}

/**
 * El corazón, compartido por los dos lugares donde se pregunta: la pestaña del
 * modal de búsqueda y la sección del Oráculo en el inicio. Recibe los elementos
 * ya encontrados y se encarga del resto — mandar, esperar, pintar y los atajos.
 */
function conectar({ raiz, url, entrada, enviar, salida, estilo = 'fila' }) {
  if (raiz.dataset.svgMl) ICONOS.ml = raiz.dataset.svgMl;
  if (raiz.dataset.svgAz) ICONOS.az = raiz.dataset.svgAz;

  let pedido = null;
  // Lo último que se enseñó, para que "otra sugerencia" no repita.
  let mostrados = [];

  async function preguntar({ excluir = [] } = {}) {
    const consulta = entrada.value.trim();
    if (!consulta) {
      entrada.focus();
      return;
    }

    // Si alguien teclea rápido y manda dos veces, la primera se descarta:
    // sin esto la respuesta vieja puede llegar después y pisar a la nueva.
    pedido?.abort();
    const mio = new AbortController();
    pedido = mio;

    enviar.disabled = true;
    const dejarDePensar = pensando(salida);

    const reloj = setTimeout(() => mio.abort(), ESPERA);

    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(excluir.length ? { consulta, excluir } : { consulta }),
        signal: mio.signal,
      });
      const datos = await r.json();

      if (!r.ok) {
        error(salida, datos.error ?? 'No pudimos consultar ahorita.');
        return;
      }
      mostrados = (await pintar(raiz, salida, datos, estilo)) ?? [];
    } catch (e) {
      if (e.name === 'AbortError' && pedido !== mio) return; // la pisó una consulta nueva
      // Un bloqueador de anuncios o una extensión de privacidad corta la petición
      // al Worker sin avisar: unas veces revienta como `Failed to fetch` y otras
      // se queda colgada hasta que vence el reloj. "Revisa tu conexión" mandaba a
      // la gente a buscar el problema donde no estaba — le pasó a Brandon en su
      // propio navegador mientras en el celular funcionaba.
      error(
        salida,
        e.name === 'AbortError'
          ? 'Se tardó demasiado. Puede ser tu conexión, o una extensión bloqueando la búsqueda: pruébalo en una ventana de incógnito.'
          : 'No pudimos conectar con el buscador. Si usas un bloqueador de anuncios o una extensión de privacidad, desactívala en esta página y vuelve a intentar.'
      );
    } finally {
      dejarDePensar();
      clearTimeout(reloj);
      if (pedido === mio) {
        pedido = null;
        enviar.disabled = false;
      }
    }
  }

  enviar.addEventListener('click', () => preguntar());

  // En el modal el campo vive fuera del <form> del buscador, así que Enter no
  // envía nada por su cuenta; hay que atarlo a mano. En el Oráculo tampoco hay
  // form, por la misma razón.
  entrada.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    preguntar();
  });

  raiz.querySelectorAll('.ol-ia__ejemplo').forEach((b) => {
    b.addEventListener('click', () => {
      entrada.value = b.textContent.trim();
      preguntar();
    });
  });

  // "Sugiéreme otros": la misma pregunta, pero diciéndole al Worker cuáles ya
  // se enseñaron. Va en la llave del caché, así que de verdad vuelve a pensar.
  raiz.querySelector('[data-ol-ia-otros]')?.addEventListener('click', () => {
    if (!mostrados.length) return;
    preguntar({ excluir: mostrados });
  });

  raiz.querySelector('[data-ol-ia-limpiar]')?.addEventListener('click', () => {
    pedido?.abort();
    pedido = null;
    mostrados = [];
    entrada.value = '';
    salida.innerHTML = '';
    raiz.classList.remove('ol-ia--respondido');
    enviar.disabled = false;
    entrada.focus();
  });

  return preguntar;
}

/** La pestaña dentro del modal de búsqueda. */
function montar(raiz) {
  const panel = raiz.querySelector('[data-ol-ia-panel]');
  const entrada = raiz.querySelector('[data-ol-ia-entrada]');
  const dialogo = raiz.closest('dialog') ?? document;

  const pestaña = (cual, { enfocar = true } = {}) => {
    raiz.querySelectorAll('[data-ol-ia-tab]').forEach((b) => {
      b.setAttribute('aria-selected', String(b.dataset.olIaTab === cual));
    });
    panel.hidden = cual !== 'disfraz';
    dialogo.classList?.toggle('ol-ia-activo', cual === 'disfraz');
    if (cual === 'disfraz' && enfocar) entrada.focus({ preventScroll: true });
  };

  raiz.querySelectorAll('[data-ol-ia-tab]').forEach((b) => {
    b.addEventListener('click', () => pestaña(b.dataset.olIaTab));
  });

  // El buscador abre directo en "¿De qué te disfrazas?". Quien quiera el de
  // siempre lo tiene a un clic, pero la mayoría llega sin saber el nombre de un
  // modelo — llega sabiendo de qué se va a vestir.
  pestaña('disfraz', { enfocar: false });

  // El foco se pone cuando el diálogo abre de verdad, no al cargar la página:
  // enfocar un campo dentro de un <dialog> cerrado hace saltar el scroll.
  if (dialogo instanceof HTMLDialogElement) {
    new MutationObserver(() => {
      if (dialogo.open && !panel.hidden) entrada.focus({ preventScroll: true });
    }).observe(dialogo, { attributes: true, attributeFilter: ['open'] });
  }

  // El gancho del encabezado nace oculto en search.liquid; se revela sólo aquí,
  // o sea sólo cuando el panel existe de verdad en la página.
  document.querySelectorAll('[data-ol-gancho]').forEach((g) => g.removeAttribute('hidden'));

  conectar({
    raiz,
    url: raiz.dataset.url,
    entrada,
    enviar: raiz.querySelector('[data-ol-ia-enviar]'),
    salida: raiz.querySelector('[data-ol-ia-salida]'),
  });
}

/** El Oráculo del inicio: lo mismo, pero sin pestañas y con tarjeta grande. */
function montarOraculo(raiz) {
  conectar({
    raiz,
    url: raiz.dataset.url,
    entrada: raiz.querySelector('[data-ol-ia-entrada]'),
    enviar: raiz.querySelector('[data-ol-ia-enviar]'),
    salida: raiz.querySelector('[data-ol-ia-salida]'),
    estilo: 'tarjeta',
  });
}

/**
 * Y lo mismo para el buscador normal.
 *
 * Las tarjetas del predictive quedaron en fila, igual que las de arriba — eso se
 * hizo con CSS. Lo único que el CSS no puede es acortar el título: "Pupilentes
 * Rojo Vampiro — Combo Halloween" repetido siete veces llena la fila de palabras
 * que no distinguen nada. Lo que importa es "Rojo Vampiro".
 *
 * El predictive vuelve a pintar su lista en cada tecla, así que no basta con
 * pasar una vez: hay que quedarse mirando el contenedor. Se guarda el título
 * entero en `title` para que siga disponible al pasar el mouse, y se marca cada
 * tarjeta ya tocada para no recortar dos veces lo mismo.
 */
function acortarTitulosDelBuscador() {
  // Sin `:has([data-ol-ia])` a propósito: el buscador normal se queda en filas
  // aunque nunca se publique el Worker y la pestaña no exista.
  const contenedor = document.querySelector('.search-modal__content');
  if (!contenedor) return;

  const pasar = () => {
    contenedor
      .querySelectorAll('.predictive-search-results__card--product .resource-card__title:not([data-ol-corto])')
      .forEach((t) => {
        const entero = t.textContent.trim();
        const corto = nombreCorto(entero);
        t.dataset.olCorto = '1';
        if (corto === entero || !corto) return;
        t.title = entero;
        t.textContent = corto;
      });
  };

  pasar();
  new MutationObserver(pasar).observe(contenedor, { childList: true, subtree: true });
}

document.querySelectorAll('[data-ol-ia]').forEach(montar);
document.querySelectorAll('[data-ol-oraculo]').forEach(montarOraculo);
acortarTitulosDelBuscador();
